import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function EnhancedTeacherAttendanceView({ teacherId, teacherName, userRole, onClose }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('monthly'); // 'daily' or 'monthly'

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    if (teacherId) {
      fetchAttendanceData();
    }
  }, [teacherId, selectedMonth, selectedYear]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/teacher-attendance/${teacherId}`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setAttendanceData(response.data || []);
    } catch (error) {
      console.error('Error fetching teacher attendance:', error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = () => {
    const presentDays = attendanceData.filter(record => record.status === 'present').length;
    const absentDays = attendanceData.filter(record => record.status === 'absent').length;
    const lateDays = attendanceData.filter(record => record.status === 'late').length;
    const firstHalfDays = attendanceData.filter(record => record.status === 'first_half').length;
    const secondHalfDays = attendanceData.filter(record => record.status === 'second_half').length;
    const totalMarked = presentDays + absentDays + lateDays + firstHalfDays + secondHalfDays;
    const percentage = totalMarked > 0 ? ((presentDays + lateDays + (firstHalfDays * 0.5) + (secondHalfDays * 0.5)) / totalMarked * 100).toFixed(1) : 0;

    return {
      presentDays,
      absentDays,
      lateDays,
      firstHalfDays,
      secondHalfDays,
      totalMarked,
      percentage
    };
  };

  const getAttendanceForDate = (dateStr) => {
    return attendanceData.find(record => record.date === dateStr);
  };

  const stats = getAttendanceStats();

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      background: 'rgba(0,0,0,0.8)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '15px', 
        width: '100%', 
        maxWidth: '900px', 
        maxHeight: '90vh', 
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '20px', 
          borderBottom: '2px solid #2196f3', 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderRadius: '15px 15px 0 0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, color: '#1565c0', fontSize: '24px', fontWeight: 'bold' }}>
                📊 Teacher Attendance - {teacherName}
              </h2>
              <p style={{ margin: '5px 0 0 0', color: '#1565c0', fontSize: '16px' }}>
                Daily & Monthly Attendance Tracking
              </p>
            </div>
            <button 
              onClick={onClose}
              style={{ 
                background: '#dc2626', 
                color: 'white', 
                border: 'none', 
                padding: '10px 15px', 
                borderRadius: '50%', 
                cursor: 'pointer',
                fontSize: '18px',
                width: '45px',
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* View Mode Toggle */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '20px'
          }}>
            <div style={{ 
              background: '#f8f9fa', 
              borderRadius: '10px', 
              padding: '4px',
              display: 'flex',
              border: '2px solid #e9ecef'
            }}>
              <button
                onClick={() => setViewMode('monthly')}
                style={{
                  background: viewMode === 'monthly' ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' : 'transparent',
                  color: viewMode === 'monthly' ? 'white' : '#666',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
              >
                📊 Monthly View
              </button>
              <button
                onClick={() => setViewMode('daily')}
                style={{
                  background: viewMode === 'daily' ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' : 'transparent',
                  color: viewMode === 'daily' ? 'white' : '#666',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease'
                }}
              >
                📅 Daily View
              </button>
            </div>
          </div>

          {/* Month/Year Selector */}
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            alignItems: 'center', 
            marginBottom: '25px',
            justifyContent: 'center'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                Month:
              </label>
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '2px solid #2196f3', 
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                Year:
              </label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  border: '2px solid #2196f3', 
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {[2023, 2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <div style={{ fontSize: '18px' }}>Loading attendance data...</div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '15px', 
                marginBottom: '25px' 
              }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.presentDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Present Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.absentDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Absent Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.lateDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Late Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.firstHalfDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>First Half</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #673ab7 0%, #512da8 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(103, 58, 183, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.secondHalfDays}
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Second Half</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
                    {stats.percentage}%
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Attendance %</div>
                </div>
              </div>

              {/* Daily Calendar View */}
              {viewMode === 'daily' && (
                <div style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginBottom: '20px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '18px', textAlign: 'center' }}>
                    📅 Daily Attendance Calendar - {monthNames[selectedMonth - 1]} {selectedYear}
                  </h4>
                  
                  {/* Calendar Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '8px' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} style={{ 
                        textAlign: 'center', 
                        fontSize: '12px', 
                        fontWeight: '600', 
                        padding: '8px', 
                        color: '#666',
                        background: '#f8f9fa',
                        borderRadius: '4px'
                      }}>
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                    {(() => {
                      const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
                      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                      const days = [];
                      
                      // Empty cells for days before month starts
                      for (let i = 0; i < firstDay; i++) {
                        days.push(
                          <div key={`empty-${i}`} style={{ padding: '8px' }}></div>
                        );
                      }
                      
                      // Days of the month
                      for (let day = 1; day <= daysInMonth; day++) {
                        const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const attendance = getAttendanceForDate(dateStr);
                        const isToday = new Date().toDateString() === new Date(selectedYear, selectedMonth - 1, day).toDateString();
                        const isWeekend = new Date(selectedYear, selectedMonth - 1, day).getDay() === 0; // Sunday
                        
                        days.push(
                          <div
                            key={day}
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              borderRadius: '6px',
                              background: isWeekend ? '#ffebee' :
                                         attendance?.status === 'present' ? '#e8f5e8' :
                                         attendance?.status === 'late' ? '#fff3e0' :
                                         attendance?.status === 'first_half' ? '#f3e5f5' :
                                         attendance?.status === 'second_half' ? '#ede7f6' :
                                         attendance?.status === 'absent' ? '#ffebee' : '#f8f9fa',
                              color: isWeekend ? '#c62828' :
                                     attendance?.status === 'present' ? '#2e7d32' :
                                     attendance?.status === 'late' ? '#ef6c00' :
                                     attendance?.status === 'first_half' ? '#7b1fa2' :
                                     attendance?.status === 'second_half' ? '#512da8' :
                                     attendance?.status === 'absent' ? '#c62828' : '#666',
                              fontSize: '14px',
                              fontWeight: attendance ? 'bold' : 'normal',
                              border: isToday ? '2px solid #2196f3' : '1px solid rgba(0,0,0,0.1)',
                              position: 'relative',
                              minHeight: '40px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: attendance ? 'pointer' : 'default'
                            }}
                            title={isWeekend ? `${day} - Weekend` :
                                  attendance ? `${day} - ${attendance.status.toUpperCase()}${attendance.remarks ? ` (${attendance.remarks})` : ''}` :
                                  `${day} - Not marked`}
                          >
                            <div>{day}</div>
                            {attendance && (
                              <div style={{
                                fontSize: '10px',
                                fontWeight: 'bold',
                                marginTop: '2px'
                              }}>
                                {attendance.status === 'present' ? 'P' :
                                 attendance.status === 'late' ? 'L' :
                                 attendance.status === 'first_half' ? 'FH' :
                                 attendance.status === 'second_half' ? 'SH' : 'A'}
                              </div>
                            )}
                            {isWeekend && (
                              <div style={{
                                fontSize: '8px',
                                marginTop: '2px'
                              }}>
                                OFF
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return days;
                    })()}
                  </div>
                  
                  {/* Legend */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '15px', 
                    marginTop: '15px',
                    fontSize: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '12px', height: '12px', background: '#e8f5e8', borderRadius: '2px' }}></div>
                      <span>Present</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '12px', height: '12px', background: '#fff3e0', borderRadius: '2px' }}></div>
                      <span>Late</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '12px', height: '12px', background: '#f3e5f5', borderRadius: '2px' }}></div>
                      <span>First Half</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '12px', height: '12px', background: '#ede7f6', borderRadius: '2px' }}></div>
                      <span>Second Half</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '12px', height: '12px', background: '#ffebee', borderRadius: '2px' }}></div>
                      <span>Absent</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '12px', height: '12px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '2px' }}></div>
                      <span>Not Marked</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Monthly Records List */}
              {viewMode === 'monthly' && (
                <div style={{ 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginBottom: '20px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '18px' }}>
                    📋 Monthly Records - {monthNames[selectedMonth - 1]} {selectedYear}
                  </h4>
                  
                  {attendanceData.length > 0 ? (
                    <div style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto', 
                      border: '1px solid #ddd', 
                      borderRadius: '8px',
                      background: 'white'
                    }}>
                      {attendanceData.slice().reverse().map(record => (
                        <div key={record._id} style={{ 
                          padding: '12px', 
                          borderBottom: '1px solid #eee', 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <strong style={{ fontSize: '16px' }}>
                              {new Date(record.date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </strong>
                            {record.remarks && (
                              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                                Remarks: {record.remarks}
                              </p>
                            )}
                            <small style={{ color: '#888', fontSize: '12px' }}>
                              Marked by: {record.markedBy}
                            </small>
                          </div>
                          <span style={{ 
                            padding: '6px 12px', 
                            borderRadius: '20px', 
                            fontSize: '12px', 
                            fontWeight: 'bold',
                            background: record.status === 'present' ? '#4caf50' : 
                                       record.status === 'late' ? '#ff9800' :
                                       record.status === 'first_half' ? '#9c27b0' :
                                       record.status === 'second_half' ? '#673ab7' : '#f44336',
                            color: 'white'
                          }}>
                            {record.status === 'first_half' ? 'FIRST HALF' :
                           record.status === 'second_half' ? 'SECOND HALF' :
                           record.status.toUpperCase()}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      color: '#666',
                      background: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '15px' }}>📅</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                        No Attendance Records
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        No attendance has been marked for {monthNames[selectedMonth - 1]} {selectedYear}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Performance Summary */}
              {stats.totalMarked > 0 && (
                <div style={{ 
                  background: stats.percentage >= 90 ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)' :
                             stats.percentage >= 75 ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' :
                             'linear-gradient(135deg, #f44336 0%, #e53935 100%)',
                  color: 'white', 
                  padding: '20px', 
                  borderRadius: '12px',
                  textAlign: 'center',
                  marginBottom: '15px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                    📊 Performance Summary
                  </h4>
                  <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                    <div>Total Days Marked: {stats.totalMarked}</div>
                    <div>Attendance Rate: {stats.percentage}%</div>
                    <div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.9 }}>
                      {stats.percentage >= 90 ? '🌟 Excellent Attendance!' :
                       stats.percentage >= 75 ? '👍 Good Attendance' :
                       '⚠️ Needs Improvement'}
                    </div>
                  </div>
                </div>
              )}

              {/* Usage Tips */}
              <div style={{ 
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
                padding: '15px', 
                borderRadius: '12px',
                border: '1px solid #2196f3'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#1565c0', fontSize: '16px' }}>
                  💡 Usage Tips
                </h4>
                <div style={{ fontSize: '14px', color: '#1976d2', lineHeight: '1.5' }}>
                  <div>• <strong>Monthly View:</strong> See overall statistics and detailed records list</div>
                  <div>• <strong>Daily View:</strong> Visual calendar showing attendance patterns</div>
                  <div>• <strong>Focus Areas:</strong> Track punctuality and identify improvement opportunities</div>
                  <div>• <strong>Records:</strong> Review specific dates and any remarks from administration</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EnhancedTeacherAttendanceView;